import { useState, useEffect } from "react";
import { 
        makeBEM, 
        titleInputValid, 
        getDateProp, 
        fadeOutThenExecute,
        getDefaultFormMsg, 
        fadeOutFormMsg, 
        gracefulFinally
} from "../global/utils";
import { LABELS, FORM_MESSAGES, UTILITY_CSS_CLASSES } from "../global/constants";
import { apiResultSuccess, newHabit, deleteHabit, editHabit } from "../apihelper";
import FormStatus from "../atoms/FormStatus";
import FormButton from "../atoms/FormButton";
import { ConfirmDialog } from '../components/Dialogs';

function HabitForm({
                    className='',
                    boardID=-1,
                    habit,
                    deleteSuccess,
                    discardForm,
                    controller
                }) {

    const moduleName = 'habit-form';

    const startDateDefaultValue = getDateProp(new Date(), 'date');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formData, setFormData] = useState({ title: '', start_date: startDateDefaultValue, duration_days: 27 });
	const [errors, setErrors] = useState({});
	const [formStatus, setFormStatus] = useState( LABELS.habitFormInstructions );
    const [editMode, setEditMode] = useState(false);
    const [fadeOutTimer, setFadeOutTimer] = useState(false);
    const [ctrl, setCtrl] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const formWrapperClass = `form ${moduleName} ${className}`;
    const inputsWrapperClass = `${makeBEM('form', 'inputs-wrapper')} ${makeBEM(moduleName, 'inputs-wrapper')}`;
    const titleErrorClass = errors.title ? UTILITY_CSS_CLASSES.hasErrors : '';
	const titleInputClass = `input ${makeBEM(moduleName, 'title-input')} ${titleErrorClass}`;
    const startDateErrorClass = errors.startDate ? UTILITY_CSS_CLASSES.hasErrors : '';
    const startDateInputClass = `input ${makeBEM(moduleName, 'start-date-input')} ${startDateErrorClass}`;
    const durationErrorClass = errors.duration_days ? UTILITY_CSS_CLASSES.hasErrors : '';
    const durationInputClass = `input ${makeBEM(moduleName, 'duration-input')} ${durationErrorClass}`;

    const showDeleteBtn = editMode;
    const btnsDisabled = isSubmitting || isDeleting;
    
    useEffect(() => {
        if ( habit ) {
            setFormData(habit);
            setEditMode(true);
        }
    }, [habit])

    useEffect(() => {
        if ( controller ) {
            setCtrl(controller);
        }
    }, [controller])

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedFormData = { ...formData, [name]: value };
        setFormData(updatedFormData);
    }

    // Validates form
    // Checks for title, start_date and duration_days
    const validateForm = () => {
		const newErrors = {};

		if ( !formData.title ) {
			newErrors.title = FORM_MESSAGES.titleRequired;
		} else { 
            if ( !titleInputValid(formData.title) ) {
                newErrors.title = FORM_MESSAGES.titleInvalid;
            }
        }

        if ( !formData.start_date ) {
            newErrors.startDate = FORM_MESSAGES.startDateRequired;
        }

        if ( !formData.duration_days ) {
            newErrors.duration_days = FORM_MESSAGES.durationRequired;
        } else {
            if ( parseInt(formData.duration_days) === 0 ) {
                newErrors.duration_days = FORM_MESSAGES.durationInvalid;
            }
        }

		return newErrors;
	}

    // Displays form status, in the following priority:
    // Errors | Message | Instructrions
    const getFormStatus = () => {
		if ( errors.title ) {
			return errors.title
		}

        if ( errors.startDate ) {
            return errors.startDate;
        }
        
        if ( errors.duration_days ) {
            return errors.duration_days;
        }

		if ( formStatus ) {
			return formStatus;
		}

        return getDefaultFormMsg(editMode, FORM_MESSAGES.habitEdit, FORM_MESSAGES.habitFormInstructions);
	}

    // Submits form, calls API to create a new Habit
    // New Habit result gets appended to controller
    // Then fades out form
    const submitNew = (postData) => {
        setIsSubmitting(true);
        setFormStatus( FORM_MESSAGES.newHabitSubmit );

        try {
            newHabit(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus(FORM_MESSAGES.createdNewHabit);

                        if ( result.habit ) {
                            if ( ctrl ) {
                                ctrl.addNew(result.habit);
                            }
                        }

                        setFadeOutTimer(
                            fadeOutThenExecute(discardForm)
                        );
                    } else {
                        setFormStatus( result.message )
                    }
                },
                postData
            )
        } catch (error) {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    // Submits form, calls API to save edited Habit
    // Habit response is sent to the controller
    // Then fades out the form message
    const submitEdit = (postData) => {
        setIsSubmitting(true);
        setFormStatus( FORM_MESSAGES.editHabitSubmit );
        
        try {
            editHabit(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus( FORM_MESSAGES.savedHabit );

                        if ( result.habit ) {
                            if ( ctrl ) {
                                ctrl.editHabit(result.habit);
                            }
                            fadeOutFormMsg(setFormStatus, '');
                        }
                    } else {
                        setFormStatus( result.message );
                    }
                },
                postData
            )
        } catch (error) {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsSubmitting);
        }
    }

    // Handles form submit, 
    // Calls submitNew, submitEdit based on editMode state
    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateForm();

         if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
        } else {
            setErrors({});

            const postData = { ...formData, board_id: boardID };

            if ( !editMode ) {
                submitNew(postData);
            } else {
                submitEdit(postData);
            }
            
        }
    }

    const handleDelete = () => {
        discardConfirmDialog();
        const postData = { ...formData };
        setIsDeleting(true);

        try {
            deleteHabit(
                (result) => {
                    if ( apiResultSuccess(result) ) {
                        setFormStatus( FORM_MESSAGES.deletedHabit );

                        if ( deleteSuccess ) {
                            setFadeOutTimer(
                                fadeOutThenExecute(() => {
                                    deleteSuccess(postData.habit_id);
                                })
                            );
                        } 
                    } else {
                        setFormStatus( result.message );
                    }
                }, 
                postData
            )
        } catch {
            setFormStatus( FORM_MESSAGES.failedSubmit );
        } finally {
            gracefulFinally(setIsDeleting);
        }
    }

    const resetForm = () => {
        setFormData({ content: '' });
        setErrors({});
    }

    const handleDiscard = () => {
        resetForm();
        if ( discardForm ) {
            discardForm();
        }

        if ( fadeOutTimer ) {
            clearTimeout(fadeOutTimer);
        }
    }

    const promptDeleteConfirm = () => {
        setShowConfirmDialog(true);
    }

    const discardConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

    return (
        <div className={ formWrapperClass }>
            <form onSubmit={ handleSubmit }>
                <div className={ inputsWrapperClass }>
                    <FormStatus
                        errors={errors}
                        formStatus= { getFormStatus() }
                        editMode={editMode}
                        customClass={ makeBEM(moduleName, 'form-status') }
                    />

                    <input
                        id="title"
                        name="title"
                        type="text"
                        className={titleInputClass}
                        onChange={handleChange}
                        autoComplete="off"
                        required
                        placeholder={ LABELS.habitTitlePlaceholder }
                        value={ formData.title } 
                        autoFocus
                    />

                    <div className="form__input-group form__input-group--col-two-one">
                        <div className={ `${makeBEM('form', 'date-input-wrapper')} ${makeBEM(moduleName, 'date-input-wrapper')}` }>
                            <label
                                className={ `label ${makeBEM(moduleName, 'start-date-label')}` } 
                                htmlFor="start_date">{LABELS.startDate}
                            </label>
                            <input
                                id="start_date"
                                name="start_date"
                                type="date"
                                className={startDateInputClass}
                                onChange={handleChange}
                                value={ formData.start_date ? formData.start_date : '' }
                                placeholder="Start Date"
                            />
                        </div>

                        <div className={ `${makeBEM('form', 'input-with-label-wrapper')} ${makeBEM(moduleName, 'duration-input-wrapper')}` }>
                            <label
                                className={ `label ${makeBEM(moduleName, 'duration-label')}` } 
                                htmlFor="duration_days">{LABELS.duration}
                            </label>
                            <input 
                                id="duration_days"
                                name="duration_days"
                                type="number"
                                className={durationInputClass}
                                onChange={handleChange}
                                value={ formData.duration_days }
                            />
                        </div>
                    </div>

                    <div className={ `${makeBEM('form', 'btns-wrapper')} ${makeBEM(moduleName, 'btns-wrapper')}` }>
                        <FormButton
                            customClass={ makeBEM(moduleName, 'submit-btn') }
                            type="submit"
                            onClick={handleSubmit}
                            disabled={btnsDisabled}
                            isSubmitting={isSubmitting}
                        />
                        <FormButton
                            customClass={ makeBEM(moduleName, 'discard-btn') }
                            onClick={handleDiscard}
                            disabled={btnsDisabled}
                            type="button"
                            icon="close"
                        />
                        
                        {
                            showDeleteBtn &&
                            <FormButton
                                customClass={ makeBEM(moduleName, 'delete-btn') }
                                onClick={promptDeleteConfirm}
                                disabled={btnsDisabled}
                                isSubmitting={isDeleting}
                                type="button"
                                icon="delete"
                            />

                        }

                    </div>
                </div>
            </form>

            {
                showConfirmDialog &&
                <ConfirmDialog
                    show={showConfirmDialog}
                    message={LABELS.confirmDelete}
                    confirmAction={handleDelete}
                    discardAction={discardConfirmDialog}
                />
            }
        </div>
    );
}

export default HabitForm;